import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { FunnelSimple, MapPin } from 'phosphor-react';
import moment from 'moment';
import { useDebounceCallback } from 'usehooks-ts';
import { FilterDrawer } from '../../../../../components/FilterDrawer';
import { PageBox } from '../../../../../components/pageBox';
import { Paginator } from '../../../../../components/paginator';
import { ReturnButton } from '../../../../../components/buttons/ReturnButton';
import { MonthNavigator } from '../../../../../components/shared/MonthNavigator';
import SelectInputForm from '../../../../../components/inputs/forms/SelectInputForm';
import { SearchModeBar, MODES_ID_NOME_CNPJ } from '../../../../../components/SearchModeBar';
import { useMonthFilter } from '../../../../../hooks/useMonthFilter';
import { useIcms } from '../../../../../hooks/useIcms';
import { useCompanies } from '../../../../../hooks/useCompanies';
import { getFormattedLabelString } from '../../../../../utils/FormatFunctions';
import { ISelect } from '../../../../../types/shared';
import { DifalCarteiraKpisCards } from './components/DifalKpis';
import { DifalEmpresaCard } from './components/DifalEmpresaCard';
import { UF_LIST } from '../../../../../utils/constants';

type SearchMode = 'ID' | 'Nome' | 'CNPJ';

const maskCnpj = (value: string): string => {
  const digits = value
    .replace(/[^0-9A-Za-z]/g, '')
    .toUpperCase()
    .slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  }
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

const COMPANY_FILTER_KEYS = [
  'cidade',
  'uf',
  'grupo_de_empresas',
  'tags',
  'comercial',
  'contabil',
  'financeiro',
  'contas_a_pagar',
  'encerramento_anual',
  'fiscal',
  'imposto_de_renda',
  'integracao_contabil',
  'integracao_fiscal',
  'pessoal',
  'relacionamento_com_cliente',
  'societario',
] as const;

type CompanyFilterKey = (typeof COMPANY_FILTER_KEYS)[number];

type FilterFormValues = Record<CompanyFilterKey, ISelect<string> | undefined> & {
  uf_destino: string;
};

interface ICompaniesFilters {
  cidade?: string[];
  uf?: string[];
  grupo_de_empresas?: string[];
  tags?: string[];
  comercial?: string[];
  contabil?: string[];
  financeiro?: string[];
  contas_a_pagar?: string[];
  encerramento_anual?: string[];
  fiscal?: string[];
  imposto_de_renda?: string[];
  integracao_contabil?: string[];
  integracao_fiscal?: string[];
  pessoal?: string[];
  relacionamento_com_cliente?: string[];
  societario?: string[];
}

export function IcmsDifalCarteiraPage() {
  const navigate = useNavigate();
  const { getDifalCarteiraKpis, getDifalCarteiraEmpresas } = useIcms();
  const { getCompaniesFilters } = useCompanies();

  const { currentDate, dateStart, dateEnd, handlePrevMonth, handleNextMonth, handleSelectMonth } =
    useMonthFilter({ defaultToPreviousMonth: true });

  const [searchParams, setSearchParams] = useSearchParams();
  const ufDestino = searchParams.get('uf_destino') || '';
  const searchMode = (searchParams.get('search_mode') as SearchMode) || 'ID';
  const searchValueParam = searchParams.get('q') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const size = parseInt(searchParams.get('size') || '50', 10);

  const [searchInput, setSearchInput] = useState(() =>
    searchMode === 'CNPJ' ? maskCnpj(searchValueParam) : searchValueParam,
  );

  useEffect(() => {
    setSearchInput(searchMode === 'CNPJ' ? maskCnpj(searchValueParam) : searchValueParam);
  }, [searchValueParam, searchMode]);

  const debouncedSearch = useDebounceCallback((value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('q', value);
    else next.delete('q');
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  }, 400);

  const handleSearchInputChange = (raw: string) => {
    let next = raw;
    if (searchMode === 'CNPJ') next = maskCnpj(raw);
    else if (searchMode === 'ID') next = raw.replace(/\D/g, '');
    setSearchInput(next);
    debouncedSearch(next);
  };

  const handleSearchModeChange = (mode: SearchMode) => {
    debouncedSearch.cancel?.();
    setSearchInput('');
    const next = new URLSearchParams(searchParams);
    next.set('search_mode', mode);
    next.delete('q');
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const {
    isOpen: isFilterDrawerOpen,
    onOpen: onFilterDrawerOpen,
    onClose: onFilterDrawerClose,
  } = useDisclosure();

  const getFilterDefault = (key: CompanyFilterKey): ISelect<string> | undefined => {
    const value = searchParams.get(key);
    return value ? { label: value, value } : undefined;
  };

  const filtersFormMethods = useForm<FilterFormValues>({
    mode: 'onChange',
    defaultValues: {
      uf_destino: ufDestino,
      cidade: getFilterDefault('cidade'),
      uf: getFilterDefault('uf'),
      grupo_de_empresas: getFilterDefault('grupo_de_empresas'),
      tags: getFilterDefault('tags'),
      comercial: getFilterDefault('comercial'),
      contabil: getFilterDefault('contabil'),
      financeiro: getFilterDefault('financeiro'),
      contas_a_pagar: getFilterDefault('contas_a_pagar'),
      encerramento_anual: getFilterDefault('encerramento_anual'),
      fiscal: getFilterDefault('fiscal'),
      imposto_de_renda: getFilterDefault('imposto_de_renda'),
      integracao_contabil: getFilterDefault('integracao_contabil'),
      integracao_fiscal: getFilterDefault('integracao_fiscal'),
      pessoal: getFilterDefault('pessoal'),
      relacionamento_com_cliente: getFilterDefault('relacionamento_com_cliente'),
      societario: getFilterDefault('societario'),
    },
  });

  const ufDestinoWatch = useWatch({ name: 'uf_destino', control: filtersFormMethods.control });

  useEffect(() => {
    COMPANY_FILTER_KEYS.forEach(field => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        filtersFormMethods.setValue(field, { label: urlValue, value: urlValue });
      } else {
        filtersFormMethods.setValue(field, undefined);
      }
    });
    filtersFormMethods.setValue('uf_destino', ufDestino);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, ufDestino]);

  const { data: companiesFilters } = useQuery<ICompaniesFilters>({
    queryKey: ['companies-filters'],
    queryFn: getCompaniesFilters,
    staleTime: 5 * 60 * 1000,
  });

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (ufDestino) count += 1;
    COMPANY_FILTER_KEYS.forEach(key => {
      if (searchParams.get(key)) count += 1;
    });
    return count;
  }, [searchParams, ufDestino]);
  const hasActiveFilters = activeFiltersCount > 0;

  const handleApplyFilters = () => {
    const values = filtersFormMethods.getValues();
    const next = new URLSearchParams(searchParams);

    if (values.uf_destino) next.set('uf_destino', values.uf_destino);
    else next.delete('uf_destino');

    COMPANY_FILTER_KEYS.forEach(key => {
      const v = values[key];
      if (v?.value) next.set(key, v.value);
      else next.delete(key);
    });

    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const handleClearFilters = () => {
    filtersFormMethods.reset({
      uf_destino: '',
      cidade: undefined,
      uf: undefined,
      grupo_de_empresas: undefined,
      tags: undefined,
      comercial: undefined,
      contabil: undefined,
      financeiro: undefined,
      contas_a_pagar: undefined,
      encerramento_anual: undefined,
      fiscal: undefined,
      imposto_de_renda: undefined,
      integracao_contabil: undefined,
      integracao_fiscal: undefined,
      pessoal: undefined,
      relacionamento_com_cliente: undefined,
      societario: undefined,
    });
    const next = new URLSearchParams(searchParams);
    next.delete('uf_destino');
    COMPANY_FILTER_KEYS.forEach(key => next.delete(key));
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const baseFilters = useMemo(() => {
    const params: Record<string, string> = {
      dt_inicio: dateStart,
      dt_fim: dateEnd,
    };
    if (ufDestino) params.uf_destino = ufDestino;
    if (searchValueParam) {
      if (searchMode === 'CNPJ')
        params.cnpj = searchValueParam.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
      else if (searchMode === 'ID') params.codi_emp = searchValueParam;
      else params.search = searchValueParam;
    }
    COMPANY_FILTER_KEYS.forEach(key => {
      const v = searchParams.get(key);
      if (v) params[key] = v;
    });
    return params;
  }, [dateStart, dateEnd, ufDestino, searchMode, searchValueParam, searchParams]);

  const { data: kpisData, isLoading: isLoadingKpis } = useQuery({
    queryKey: ['difal-carteira-kpis', baseFilters],
    queryFn: getDifalCarteiraKpis,
    enabled: !!dateStart && !!dateEnd,
    staleTime: 5 * 60 * 1000,
  });

  const tableQuery = useQuery({
    queryKey: ['difal-carteira-empresas', { ...baseFilters, page, size }],
    queryFn: getDifalCarteiraEmpresas,
    enabled: !!dateStart && !!dateEnd,
    placeholderData: prev => prev,
  });

  const items = tableQuery.data?.data?.items ?? [];
  const pagination = {
    total: tableQuery.data?.data?.total ?? 0,
    pages: tableQuery.data?.data?.pages ?? 0,
    page: tableQuery.data?.data?.page ?? 1,
    size: tableQuery.data?.data?.size ?? size,
    next_page: tableQuery.data?.data?.next_page ?? null,
    previous_page: tableQuery.data?.data?.previous_page ?? null,
  };

  const isInitialLoading = tableQuery.isFetching && items.length === 0;
  const isEmpty = !tableQuery.isFetching && items.length === 0;

  const renderList = () => {
    if (isInitialLoading) {
      return (
        <Flex direction="column" align="center" justify="center" py={20} gap={3}>
          <Spinner size="xl" color="primary" />
          <Text fontSize="md" fontWeight="semibold" color="gray.600">
            Carregando carteira...
          </Text>
        </Flex>
      );
    }
    if (isEmpty) {
      return (
        <Flex justify="center" py={20}>
          <Text fontSize="md" color="gray.500">
            Nenhuma empresa com DIFAL no período.
          </Text>
        </Flex>
      );
    }
    return (
      <>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={3}>
          {items.map(empresa => (
            <DifalEmpresaCard
              key={empresa.codi_emp}
              empresa={empresa}
              onClick={emp =>
                navigate(`/modulesHome/supervisor/icms/difal/empresa/${emp.codi_emp}`)
              }
            />
          ))}
        </SimpleGrid>
        {pagination.total > size && <Paginator pagination={pagination} />}
      </>
    );
  };

  return (
    <PageBox>
      <Flex align="center" pb={5} wrap="wrap" gap={2}>
        <ReturnButton previousPage="/modulesHome/supervisor/icms" />
        <Box h="full" flexShrink={0}>
          <Flex align="center" justify="center" borderRadius="full" p={3} bg="#FFF3E0">
            <MapPin color="#F57C00" className="size-8" />
          </Flex>
        </Box>
        <Flex direction="column" justify="center" gap={1} pl={2} flex="1" minW={0}>
          <Text fontWeight="bold" fontSize={{ base: '14px', md: '16px' }}>
            DIFAL · Carteira
          </Text>
          <Text fontSize={{ base: '11px', md: '12px' }} color="gray.500">
            Empresas com DIFAL no período {moment(dateStart).format('DD/MM/YYYY')} a{' '}
            {moment(dateEnd).format('DD/MM/YYYY')}
          </Text>
        </Flex>
      </Flex>

      <Box mb={4} p={4} bg="gray.50" borderRadius="lg">
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', md: 'center' }}
          gap={{ base: 3, md: 4 }}
        >
          <HStack spacing={0} flex={1} maxW={{ base: '100%', md: '350px' }}>
            <SearchModeBar
              modes={MODES_ID_NOME_CNPJ}
              mode={searchMode}
              onModeChange={m => handleSearchModeChange(m as SearchMode)}
              value={searchInput}
              onValueChange={handleSearchInputChange}
              inputMode={searchMode === 'ID' ? 'numeric' : 'text'}
              maxLength={searchMode === 'CNPJ' ? 18 : undefined}
            />
          </HStack>

          <HStack spacing={3} flexWrap="wrap" w={{ base: '100%', md: 'auto' }}>
            <Box flex={{ base: 1, md: 'none' }}>
              <MonthNavigator
                currentDate={currentDate}
                onPrevMonth={handlePrevMonth}
                onNextMonth={handleNextMonth}
                onSelectMonth={handleSelectMonth}
              />
            </Box>
            <Button
              onClick={onFilterDrawerOpen}
              leftIcon={<FunnelSimple size={16} />}
              variant="outline"
              colorScheme={hasActiveFilters ? 'blue' : 'gray'}
              height="38px"
              fontSize="sm"
            >
              Filtros{hasActiveFilters && ` (${activeFiltersCount})`}
            </Button>
            {(isLoadingKpis || tableQuery.isFetching) && <Spinner size="sm" color="primary" />}
          </HStack>
        </Flex>
      </Box>

      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={onFilterDrawerClose}
        title="Filtros Avançados"
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        onApplyFilters={handleApplyFilters}
        formMethods={filtersFormMethods}
        size="lg"
      >
        <FormProvider {...filtersFormMethods}>
          <Box mb={5}>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="semibold">
                UF de destino (DIFAL)
              </FormLabel>
              <Select
                value={ufDestinoWatch || ''}
                onChange={e => filtersFormMethods.setValue('uf_destino', e.target.value)}
                placeholder="Todas as UFs de destino"
                bg="white"
              >
                {UF_LIST.map(u => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </Select>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Filtra empresas com DIFAL recolhido para a UF selecionada.
              </Text>
            </FormControl>
          </Box>

          <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={3}>
            Filtros de empresa
          </Text>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
            <SelectInputForm<ISelect<string>>
              name="cidade"
              placeholder="Filtrar por cidade"
              options={getFormattedLabelString(companiesFilters?.cidade)}
              controlMb={0}
              isClearable
            />
            <SelectInputForm<ISelect<string>>
              name="uf"
              placeholder="Filtrar por Estado"
              options={getFormattedLabelString(companiesFilters?.uf)}
              controlMb={0}
              isClearable
            />
            <SelectInputForm<ISelect<string>>
              name="grupo_de_empresas"
              placeholder="Filtrar por grupo de empresa"
              options={getFormattedLabelString(companiesFilters?.grupo_de_empresas)}
              controlMb={0}
              isClearable
            />
            <SelectInputForm<ISelect<string>>
              name="tags"
              placeholder="Filtrar por tags"
              options={getFormattedLabelString(companiesFilters?.tags)}
              controlMb={0}
              isClearable
            />
            <SelectInputForm<ISelect<string>>
              name="comercial"
              placeholder="Filtrar por comercial"
              options={getFormattedLabelString(companiesFilters?.comercial)}
              controlMb={0}
              isClearable
            />
            <SelectInputForm<ISelect<string>>
              name="contabil"
              placeholder="Filtrar pelo contábil"
              options={getFormattedLabelString(companiesFilters?.contabil)}
              controlMb={0}
              isClearable
            />
            <SelectInputForm<ISelect<string>>
              name="financeiro"
              placeholder="Filtrar pelo financeiro"
              options={getFormattedLabelString(companiesFilters?.financeiro)}
              controlMb={0}
              isClearable
            />
            <SelectInputForm<ISelect<string>>
              name="contas_a_pagar"
              placeholder="Filtrar por contas a pagar"
              options={getFormattedLabelString(companiesFilters?.contas_a_pagar)}
              controlMb={0}
              isClearable
            />
            <SelectInputForm<ISelect<string>>
              name="encerramento_anual"
              placeholder="Filtrar por encerramento anual"
              options={getFormattedLabelString(companiesFilters?.encerramento_anual)}
              controlMb={0}
              isClearable
            />
            <SelectInputForm<ISelect<string>>
              name="fiscal"
              placeholder="Filtrar por fiscal"
              options={getFormattedLabelString(companiesFilters?.fiscal)}
              controlMb={0}
              isClearable
            />
            <SelectInputForm<ISelect<string>>
              name="imposto_de_renda"
              placeholder="Filtrar por imposto de renda"
              options={getFormattedLabelString(companiesFilters?.imposto_de_renda)}
              controlMb={0}
              isClearable
            />
            <SelectInputForm<ISelect<string>>
              name="integracao_contabil"
              placeholder="Filtrar por integração contábil"
              options={getFormattedLabelString(companiesFilters?.integracao_contabil)}
              controlMb={0}
              isClearable
            />
            <SelectInputForm<ISelect<string>>
              name="integracao_fiscal"
              placeholder="Filtrar por integração fiscal"
              options={getFormattedLabelString(companiesFilters?.integracao_fiscal)}
              controlMb={0}
              isClearable
            />
            <SelectInputForm<ISelect<string>>
              name="pessoal"
              placeholder="Filtrar por pessoal"
              options={getFormattedLabelString(companiesFilters?.pessoal)}
              controlMb={0}
              isClearable
            />
            <SelectInputForm<ISelect<string>>
              name="relacionamento_com_cliente"
              placeholder="Filtrar por relacionamento com cliente"
              options={getFormattedLabelString(companiesFilters?.relacionamento_com_cliente)}
              controlMb={0}
              isClearable
            />
            <SelectInputForm<ISelect<string>>
              name="societario"
              placeholder="Filtrar por societário"
              options={getFormattedLabelString(companiesFilters?.societario)}
              controlMb={0}
              isClearable
            />
          </SimpleGrid>
        </FormProvider>
      </FilterDrawer>

      <DifalCarteiraKpisCards data={kpisData} isLoading={isLoadingKpis} />

      {renderList()}
    </PageBox>
  );
}
